import type { Agent, AgentRole, AgentRoleInfo, AgentTask, AgentHealth, DispatcherStats, ActivityEvent, ExecutionSummary, ExecutionTask, TaskAssignment, DependencyGraph, SchedulerStats, StartExecutionRequest } from '../types'
import { fetchAPI } from './core'

// Agents
export async function listAgents(projectId?: string): Promise<{ agents: Agent[]; count: number; active: number }> {
  const params = projectId ? `?project_id=${projectId}` : ''
  return fetchAPI(`/agents${params}`)
}

export async function getAgent(id: string): Promise<Agent> {
  return fetchAPI(`/agents/${id}`)
}

export async function getAgentRoles(): Promise<{ roles: AgentRoleInfo[] }> {
  return fetchAPI('/agents/roles')
}

export async function startAgent(projectId: string, task: string, role?: AgentRole): Promise<Agent> {
  return fetchAPI('/agents', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, task, role: role || 'general' }),
  })
}

export async function stopAgent(id: string): Promise<{ message: string; agent_id: string }> {
  return fetchAPI(`/agents/${id}`, {
    method: 'DELETE',
  })
}

export async function getAgentLogs(id: string, lines?: number): Promise<{ agent_id: string; lines: string[]; count: number }> {
  const params = lines ? `?lines=${lines}` : ''
  return fetchAPI(`/agents/${id}/logs${params}`)
}

export async function getAgentActivity(id: string, limit?: number): Promise<{ agent_id: string; events: ActivityEvent[]; count: number }> {
  const params = limit ? `?limit=${limit}` : ''
  return fetchAPI(`/agents/${id}/activity${params}`)
}

export async function respondToAgent(id: string, input: string): Promise<{ message: string; agent_id: string }> {
  return fetchAPI(`/agents/${id}/respond`, {
    method: 'POST',
    body: JSON.stringify({ input }),
  })
}

export async function retryAgent(id: string): Promise<{ message: string; agent_id: string }> {
  return fetchAPI(`/agents/${id}/retry`, {
    method: 'POST',
  })
}

// Tasks
export async function listTasks(projectId?: string, status?: string): Promise<{ tasks: AgentTask[]; count: number; pending: number }> {
  const params = new URLSearchParams()
  if (projectId) params.set('project_id', projectId)
  if (status) params.set('status', status)
  const query = params.toString()
  return fetchAPI(`/tasks${query ? `?${query}` : ''}`)
}

export async function getTask(id: string): Promise<AgentTask> {
  return fetchAPI(`/tasks/${id}`)
}

export async function enqueueTask(projectId: string, type: string, description: string, priority?: number, metadata?: Record<string, unknown>): Promise<AgentTask> {
  return fetchAPI('/tasks', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, type, description, priority, metadata }),
  })
}

export async function cancelTask(id: string): Promise<{ message: string; task_id: string }> {
  return fetchAPI(`/tasks/${id}`, {
    method: 'DELETE',
  })
}

// Dispatcher
export async function getDispatcherStats(): Promise<DispatcherStats> {
  return fetchAPI('/dispatcher/stats')
}

export async function startDispatcher(): Promise<{ message: string; running: boolean }> {
  return fetchAPI('/dispatcher/start', { method: 'POST' })
}

export async function stopDispatcher(): Promise<{ message: string; running: boolean }> {
  return fetchAPI('/dispatcher/stop', { method: 'POST' })
}

export async function triggerDispatch(): Promise<{ message: string }> {
  return fetchAPI('/dispatcher/trigger', { method: 'POST' })
}

// Agent Health
export async function getAgentHealth(): Promise<{ agents: AgentHealth[]; count: number; running: boolean }> {
  return fetchAPI('/health/agents')
}

export async function getSingleAgentHealth(id: string): Promise<AgentHealth> {
  return fetchAPI(`/health/agents/${id}`)
}

export async function startHealthMonitor(): Promise<{ message: string; running: boolean }> {
  return fetchAPI('/health/agents/start', { method: 'POST' })
}

export async function stopHealthMonitor(): Promise<{ message: string; running: boolean }> {
  return fetchAPI('/health/agents/stop', { method: 'POST' })
}

export async function checkHealthNow(): Promise<{ message: string }> {
  return fetchAPI('/health/agents/check', { method: 'POST' })
}

// Execution Engine API
export async function startExecution(projectId: string, request: StartExecutionRequest): Promise<ExecutionSummary> {
  return fetchAPI(`/executions?project_id=${projectId}`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export async function listExecutions(): Promise<{ executions: ExecutionSummary[]; count: number }> {
  return fetchAPI('/executions')
}

export async function getExecution(executionId: string): Promise<ExecutionSummary> {
  return fetchAPI(`/executions/${executionId}`)
}

export async function getReadyTasks(executionId: string): Promise<{ tasks: ExecutionTask[]; count: number }> {
  return fetchAPI(`/executions/${executionId}/tasks/ready`)
}

export async function getBlockedTasks(executionId: string): Promise<{ tasks: ExecutionTask[]; count: number }> {
  return fetchAPI(`/executions/${executionId}/tasks/blocked`)
}

export async function getDependencyGraph(executionId: string): Promise<DependencyGraph> {
  return fetchAPI(`/executions/${executionId}/graph`)
}

export async function getSchedulerStats(): Promise<SchedulerStats> {
  return fetchAPI('/executions/stats')
}

export async function assignTask(executionId: string, taskId: string, agentId: string): Promise<TaskAssignment> {
  return fetchAPI(`/executions/${executionId}/tasks/${taskId}/assign`, {
    method: 'POST',
    body: JSON.stringify({ agent_id: agentId }),
  })
}

export async function completeTask(
  executionId: string,
  taskId: string,
  agentId?: string,
  summary?: string,
  filesChanged?: string[]
): Promise<{ execution_id: string; task_id: string; status: string }> {
  const params = agentId ? `?agent_id=${agentId}` : ''
  return fetchAPI(`/executions/${executionId}/tasks/${taskId}/complete${params}`, {
    method: 'POST',
    body: JSON.stringify({ summary, files_changed: filesChanged }),
  })
}

export async function failTask(
  executionId: string,
  taskId: string,
  error: string,
  agentId?: string
): Promise<{ execution_id: string; task_id: string; status: string; error: string }> {
  const params = agentId ? `?agent_id=${agentId}` : ''
  return fetchAPI(`/executions/${executionId}/tasks/${taskId}/fail${params}`, {
    method: 'POST',
    body: JSON.stringify({ error }),
  })
}
